import { Component, OnInit, ViewChild, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { globals } from '../../../../globals';
import { ISubscription } from 'rxjs/Subscription';
import { } from '@types/googlemaps';
import { ManuscriptsService } from '../../../services/manuscripts.service';
import { VocsService } from '../../../services/vocs.service';
import { AppContentsService } from '../../../services/app-contents.service';
import { VocTerms, VocTerm } from '../../../models/voc-term';
import { ManuscriptList } from '../../../models/manuscript';
import { MssListFilters } from '../../../models/mss-list-filters';
import MarkerClusterer from '@google/markerclusterer';

@Component({
  selector: 'app-home-map',
  templateUrl: './home-map.component.html',
  styleUrls: ['./home-map.component.css']
})
export class HomeMapComponent implements OnInit, OnDestroy {
    @ViewChild('googleMap') googleMapNode: any;
    @ViewChild('mapLegend') mapLegendNode: any;
    private map: google.maps.Map;
    private librariesVoc: VocTerms;
    private placesVoc: VocTerms;
    private subscriptions: Array<ISubscription> = [];
    private mssList: ManuscriptList;
    hideLegend = true;
    title: SafeHtml;
    subtitle: SafeHtml;
    markerClusterer?: MarkerClusterer;

    constructor(
        private sanitizer: DomSanitizer,
        private router: Router,
        private vocsService: VocsService,
        private manuscriptsService: ManuscriptsService,
        private zone: NgZone,
        private appContentsService: AppContentsService
    ) {
    }

    ngOnInit() {
        this.title = globals.html_small_loading_gif;
        this.subtitle = globals.html_small_loading_gif;

        /* Static contents */
        this.subscriptions.push(
            this.appContentsService.getPageContents('home')
                .subscribe(
                    page => {
                        if (page && page.name === 'home') {
                            const contents = page.contents.find(c => c.name === 'map');
                            if (contents) {
                                this.title = this.sanitizer.bypassSecurityTrustHtml(contents.title);
                                this.subtitle = this.sanitizer.bypassSecurityTrustHtml(contents.text);
                            }
                        }
                    },
                    err => console.error(err)
                )
        );

        /* Style of the map */
        const mapStyles: Array<google.maps.MapTypeStyle> = [
            {
                'featureType': 'water',
                'stylers': [
                    {
                        'visibility': 'on'
                    },
                    {
                        'color': '#71acd7'
                    },
                    {
                        'lightness': 40
                    }
                ]
            },
            {
                'featureType': 'landscape',
                'stylers': [
                    {
                        'color': '#cccccc'
                    },
                    {
                        'lightness': 40
                    }
                ]
            },
            {
                'featureType': 'road.highway',
                'elementType': 'geometry',
                'stylers': [
                    {
                        'color': '#e1e1e1'
                    },
                    {
                        'lightness': 61
                    }
                ]
            },
            {
                'featureType': 'road.arterial',
                'elementType': 'geometry',
                'stylers': [
                    {
                        'visibility': 'off'
                    }
                ]
            },
            {
                'featureType': 'road.local',
                'elementType': 'geometry',
                'stylers': [
                    {
                        'visibility': 'off'
                    }
                ]
            },
            {
                'featureType': 'poi.park',
                'elementType': 'geometry',
                'stylers': [
                    {
                        'color': '#b7cdb8'
                    }
                ]

            },
            {
                'featureType': 'administrative',
                'stylers': [
                    {
                        'visibility': 'on'
                    },
                    {
                        'lightness': 20
                    }
                ]
            },
            {
                'featureType': 'poi.park',
                'elementType': 'labels',
                'stylers': [
                    {
                        'visibility': 'off'
                    }
                ]
            },
            {
                'featureType': 'road',
                'elementType': 'labels',
                'stylers': [
                    {
                        'visibility': 'off'
                    }
                ]
            },
            {
                'featureType': 'transit',
                'elementType': 'geometry',
                'stylers': [
                    {
                        'visibility': 'off'
                    }
                ]
            },
            {
                'featureType': 'transit',
                'elementType': 'labels',
                'stylers': [
                    {
                        'visibility': 'off'
                    }
                ]
            }
        ]; // styles

        /* Map options */
        const mapOptions: google.maps.MapOptions = {
            center: new google.maps.LatLng(41.3836571, 11.4780461),
            zoom: 3,
            scrollwheel: false,
            zoomControl: true,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            mapTypeControlOptions: {
                // mapTypeIds: ['phibor_map_style'] // google.maps.MapTypeId.ROADMAP
                mapTypeIds: []
            }
        };

        /* Map creation */
        this.map = new google.maps.Map(this.googleMapNode.nativeElement, mapOptions);

        /* Styled map */
        this.map.mapTypes.set('phibor_map_style', new google.maps.StyledMapType(mapStyles, {name: 'Map'}));
        this.map.setMapTypeId('phibor_map_style');

        /* Load manuscript list */
        this.subscriptions.push(
            this.manuscriptsService.getManuscripts(false).subscribe(mssList => {
                this.mssList = mssList;
                if (this.mssList && this.mssList.length > 0) {

                    /* Init marker clustering */
                    this.markerClusterer = new MarkerClusterer(
                        this.map,
                        [],
                        {imagePath: 'assets/styles/map/m'}
                    );

                    /* Load library markers */
                    this.subscriptions.push(
                        this.vocsService.getLibraries().subscribe(
                            libraries => {
                                this.librariesVoc = libraries;
                                this.loadLibraryMarkers();
                            },
                            err => console.error(err)
                        )
                    );

                    /* Load manuscript copy place markers */
                    this.subscriptions.push(
                        this.vocsService.getMsCopyPlaces().subscribe(
                            copyPlaces => {
                                this.placesVoc = copyPlaces;
                                this.loadPlaceMarkers();
                            },
                            err => console.error(err)
                        )
                    );
                }
            },
                err => console.error(err)
            )
        );

        /* Add legend */
        this.map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(this.mapLegendNode.nativeElement);
        this.hideLegend = false;
    }

    private loadLibraryMarkers(): void {
        const __this = this;
        for (const library of this.librariesVoc) {
            const msInfo = this.mssList.find(ms => ms.fkidlibraries === library.pkid);

            const libraryInfo = '<div class="popup">'
                + (msInfo ? '<small>' + msInfo.__countries + ', ' + msInfo.__cities + '</small><br>' : '')
                + '<h2>' + library.term + '</h2>'
                + '<strong>' + library.__countMss + '</strong> manuscript' + (library.__countMss > 1 ? 's ' : ' ')
                + '<button type="button" class="btn btn-link" id="libraryMssBtn_' + library.pkid + '">'
                + 'browse <i class="fa fa-angle-right"></i></button>'
                + '</div>';

            const infoWindow = new google.maps.InfoWindow({
                content: libraryInfo,
                maxWidth: 240
            });

            google.maps.event.addListener(infoWindow, 'domready', function () {
                document.getElementById('libraryMssBtn_' + library.pkid).addEventListener('click', function (event) {
                    __this.browseLibraryMss(library);
                    event.stopPropagation();
                });
            });

            let libraryIcon = 'bib-icon-2.png';
            if (library.fkidcertainty === 2) { // imprecise
                libraryIcon = 'bib-icon.png';
            }

            const libraryMarker = new google.maps.Marker({
                position: new google.maps.LatLng(parseFloat(library.latitude), parseFloat(library.longitude)),
                map: this.map,
                icon: 'assets/styles/map/' + libraryIcon
            });

            google.maps.event.addListener(libraryMarker, 'click', function () {
                infoWindow.open(this.map, libraryMarker);
            });

            this.markerClusterer.addMarker(libraryMarker);
        }
    }

    public browseLibraryMss(library: VocTerm) {
        const mssListFilters = new MssListFilters();
        mssListFilters.library = library;
        this.manuscriptsService.updateFilters(mssListFilters);
        /* Fix needed because router navigate does not activate onInit after external request - 18/06/2018 - Angular 5.2.0 */
        this.zone.run(() => this.router.navigate(['/manuscripts/list']));
    }

    private loadPlaceMarkers(): void {
        const __this = this;
        for (const place of this.placesVoc) {

            const placeInfo = '<div class="popup">'
                + '<h2>' + place.term + '</h2>'
                + '<strong>' + place.__countMss + '</strong> manuscript' + (place.__countMss > 1 ? 's ' : ' ')
                + '<button type="button" class="btn btn-link" id="placeMssBtn_' + place.pkid + '">'
                + 'Browse place manuscripts <i class="fa fa-angle-right"></i></button>'
                + '</div>';

            const infoWindow = new google.maps.InfoWindow({
                content: placeInfo,
                maxWidth: 230
            });

            google.maps.event.addListener(infoWindow, 'domready', function () {
                document.getElementById('placeMssBtn_' + place.pkid).addEventListener('click', function (event) {
                    __this.browsePlaceMss(place);
                    event.stopPropagation();
                });
            });

            let placeIcon = 'doc-icon-3.png';
            if (place.fkidcertainty === 2) { // imprecise
                placeIcon = 'doc-icon-4.png';
            } else if (place.fkidcertainty === 3) { // uncertain
                placeIcon = 'doc-icon.png';
            }

            const placeMarker = new google.maps.Marker({
                position: new google.maps.LatLng(parseFloat(place.latitude), parseFloat(place.longitude)),
                map: this.map,
                icon: 'assets/styles/map/' + placeIcon
            });

            google.maps.event.addListener(placeMarker, 'click', function () {
                infoWindow.open(this.map, placeMarker);
            });

            this.markerClusterer.addMarker(placeMarker);
        }
    }

    public browsePlaceMss(place: VocTerm) {
        const mssListFilters = new MssListFilters();
        mssListFilters.copyPlace = place;
        this.manuscriptsService.updateFilters(mssListFilters);
        /* Fix needed because router navigate does not activate onInit after external request - 18/06/2018 - Angular 5.2.0 */
        this.zone.run(() => this.router.navigate(['/manuscripts/list']));
    }

    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            if (subscription) {
                subscription.unsubscribe();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/components/ui/home-map/home-map.component.ts